import { effectiveLocationId, type ExistenciasUsadas, type Ingredient, type InventoryCount, type InventoryCountLine } from './types';
import { computeIngredientCost, ingredientDisplayName } from './costing';
import { addMonths, monthOf, type Period } from './period';
import { round } from './units';

/**
 * Inventario mensual: valoracion de recuentos y su lectura para la P&L.
 *
 * El recuento se hace en la UNIDAD DE COMPRA de cada ingrediente ("quedan
 * 12 kg de solomillo, 3 L de AOVE"), porque es como se piensa en el almacen,
 * y se valora con el precio medio de compra que ya vive en el ingrediente.
 * El valor se CONGELA al guardar: un cambio de precio posterior no reescribe
 * lo que valian las existencias de un mes cerrado.
 */

export interface InventoryLineInput {
  ingredientId: string;
  qty: number;
}

/**
 * Valora las lineas de un recuento con los precios vigentes.
 *
 * Un ingrediente sin precio cargado no tumba el recuento: se valora a 0 y el
 * que guarda ve el cero en pantalla (mejor un cero visible que un error que
 * impida cerrar el mes).
 */
export function valueInventoryLines(
  lines: InventoryLineInput[],
  ingredients: Ingredient[],
): { lines: InventoryCountLine[]; totalValue: number } {
  const byId = new Map(ingredients.map((i) => [i.ingredientId, i]));
  const valued: InventoryCountLine[] = [];

  for (const line of lines) {
    const ing = byId.get(line.ingredientId);
    // 0 = contado y no queda nada (fase 7): entra con valor 0. Lo que no se
    // conto no viaja. Negativos o NaN, fuera.
    if (!ing || !ing.purchaseUnit || !(line.qty >= 0) || !Number.isFinite(line.qty)) continue;
    if (valued.some((v) => v.ingredientId === ing.ingredientId)) continue;

    let unitCost = 0;
    try {
      unitCost = computeIngredientCost(ing).costPerPurchaseUnit;
    } catch {
      /* sin precio: valor 0, visible en pantalla */
    }

    valued.push({
      ingredientId: ing.ingredientId,
      name: ingredientDisplayName(ing),
      qty: line.qty,
      unit: ing.purchaseUnit,
      value: round(line.qty * unitCost, 2),
    });
  }

  return {
    lines: valued,
    totalValue: round(valued.reduce((sum, l) => sum + l.value, 0), 2),
  };
}

/** Valor de las existencias a cierre de un mes (suma de centros, si hay varios). */
export function inventoryValueAt(counts: InventoryCount[], month: string): number | null {
  // Solo los CIERRES CERRADOS (fase 7): un intermedio no cierra el mes, y un
  // cierre que se está contando (se guarda solo a medias) todavía no vale.
  // Los viejos, sin tipo ni estado, son cierres cerrados.
  const delMes = counts.filter((c) => c.month === month && c.kind !== 'intermedio' && c.status !== 'abierto');
  if (delMes.length === 0) return null;
  return round(delMes.reduce((sum, c) => sum + c.totalValue, 0), 2);
}

/**
 * Existencias iniciales y finales de un periodo.
 *
 * - Finales: el recuento del mes en que TERMINA el periodo.
 * - Iniciales: el recuento del mes ANTERIOR al que empieza (lo que quedaba en
 *   el almacen la noche antes de arrancar).
 *
 * Un extremo sin recuento cuenta como 0 con tal de que exista el otro: el
 * primer mes que se inventaria no tiene "iniciales" y su consumo es compras
 * menos lo que quedo en el estante, que es exactamente lo que se busca.
 */
/**
 * Existencias para el ESTADO DE RESULTADOS (bloque R, 14 oct 2026).
 *
 * Igual que `inventoryForPeriod`, con una diferencia que importa: si falta
 * el recuento que toca, NO se pone 0. Se usa el último recuento cerrado
 * conocido y se dice (`deOtroMes`). Con «existencias finales = 0» un mes sin
 * recuento cargaba todo el almacén como gastado (octubre salía con la
 * materia prima al 50,8 %).
 *
 * El primer mes que se cuenta sigue sin iniciales (0): no hay nada antes.
 * `inventoryForPeriod` se queda como estaba para el recuento, que necesita
 * saber si el mes anterior tiene cierre propio.
 */
export function existenciasDelPeriodo(
  counts: InventoryCount[],
  period: Period,
  /**
   * Locales que se miran (los activos del ámbito). Cada uno necesita SU
   * recuento: con varios locales, el último cierre se busca local por local y
   * luego se suma, y el mes solo está completo si todos tienen el suyo.
   * Ausente = los que tengan recuentos.
   */
  locales?: readonly string[],
): { hasInventory: boolean; inicial: ExistenciasUsadas; final: ExistenciasUsadas } {
  const validos = counts.filter((c) => c.kind !== 'intermedio' && c.status !== 'abierto');
  const porLocal = new Map<string, InventoryCount[]>();
  for (const id of locales ?? []) porLocal.set(id, []);
  for (const c of validos) {
    const id = effectiveLocationId(c.locationId);
    porLocal.set(id, [...(porLocal.get(id) ?? []), c]);
  }
  const usar = (mes: string): ExistenciasUsadas => {
    let valor = 0;
    let algunoExacto = false;
    let masViejo: string | undefined;
    const faltan: string[] = [];
    for (const [id, delLocal] of porLocal) {
      const exacto = inventoryValueAt(delLocal, mes);
      if (exacto != null) { valor += exacto; algunoExacto = true; continue; }
      faltan.push(id);
      const anterior = [...new Set(delLocal.map((c) => c.month))].filter((m) => m < mes).sort().pop();
      if (!anterior) continue;
      valor += inventoryValueAt(delLocal, anterior) ?? 0;
      if (!masViejo || anterior < masViejo) masViejo = anterior;
    }
    valor = round(valor, 2);
    // Un cierre atrasado vale una estimación: se dice (14 oct 2026).
    const estimado = [...porLocal.values()].some((l) => l.some((c) => c.month === mes && c.atrasado));
    if (faltan.length === 0) return algunoExacto ? { valor, mes, ...(estimado ? { estimado: true } : {}) } : { valor };
    // Algún local sin su cierre de este mes: se usa su último recuento (nunca 0
    // por no haber contado) y se dice cuál.
    if (masViejo) return { valor, mes: masViejo, deOtroMes: true, ...(algunoExacto ? { faltanLocales: faltan } : {}) };
    return algunoExacto ? { valor, mes, faltanLocales: faltan } : { valor };
  };
  const closingMonth = monthOf(period.end);
  const inicial = usar(addMonths(monthOf(period.start), -1));
  const final = usar(closingMonth);
  return { hasInventory: validos.some((c) => c.month <= closingMonth), inicial, final };
}

export function inventoryForPeriod(
  counts: InventoryCount[],
  period: Period,
): { hasInventory: boolean; opening: number; closing: number } {
  const openingMonth = addMonths(monthOf(period.start), -1);
  const closingMonth = monthOf(period.end);
  const opening = inventoryValueAt(counts, openingMonth);
  const closing = inventoryValueAt(counts, closingMonth);
  return {
    hasInventory: opening != null || closing != null,
    opening: opening ?? 0,
    closing: closing ?? 0,
  };
}
